import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { useHostNavigate } from '../../../shared/ui';

interface PanelProps {
  title: string;
  subtitle?: ReactNode;
  action?: { label: string; to: string };
  loading?: boolean;
  error?: { message: string } | null;
  empty?: ReactNode;
  children?: ReactNode;
}

/** The chrome every panel on the portal dashboard shares. */
export function Panel({ title, subtitle, action, loading, error, empty, children }: PanelProps) {
  const hostNavigate = useHostNavigate();

  return (
    <section className="flex flex-col bg-white border border-hilevel-border rounded-lg overflow-hidden">
      <header className="flex items-start justify-between gap-3 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-hilevel-slate m-0">{title}</h3>
          {subtitle && <p className="text-xs text-hilevel-gray m-0 mt-0.5">{subtitle}</p>}
        </div>
        {action && (
          <button
            type="button"
            onClick={() => hostNavigate(action.to)}
            className="shrink-0 flex items-center gap-1 text-xs font-medium text-hilevel-gray hover:text-hilevel-red bg-transparent border-0 p-0 cursor-pointer transition-colors"
          >
            {action.label}
            <ArrowRight className="text-[10px]" />
          </button>
        )}
      </header>

      <div className="flex-1 px-4 pb-4">
        {error ? (
          <p className="text-xs text-hilevel-red m-0">{error.message}</p>
        ) : loading ? (
          <p className="text-xs text-hilevel-gray m-0">Loading…</p>
        ) : empty ? (
          <p className="text-xs text-hilevel-gray m-0">{empty}</p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="min-w-0">
      <p className="text-2xl font-semibold leading-none m-0 text-hilevel-slate">{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-hilevel-gray m-0 mt-1 truncate">
        {label}
      </p>
    </div>
  );
}
